import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TeamJersey from './TeamJersey';
import QuestionCard from './QuestionCard';
import { resolveJerseyPair, type Team } from '../utils/jersey-resolver';
import { AnswerButton } from './shared/AnswerButton';

interface PropPickProps {
  teams: [Team, Team];
  question: string;
  subtext?: string;
  selectedAnswer?: 'yes' | 'no';
  onAnswerSelect: (answer: 'yes' | 'no') => void;
  points: string | number;
  earnedPoints?: number;
  percentages?: { yes: number; no: number };
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionId?: string;
  questionNumber?: number;
}

const PropPick: React.FC<PropPickProps> = ({
  teams,
  question,
  subtext,
  selectedAnswer,
  onAnswerSelect,
  points,
  earnedPoints,
  percentages,
  isCorrect,
  disabled = false,
  className = '',
  questionId,
  questionNumber,
}) => {
  const { t } = useTranslation();
  const [team1, team2] = teams;

  // Memoize team jersey data to prevent random changes and unnecessary re-renders
  const teamJerseys = useMemo(() => resolveJerseyPair(team1, team2), [team1, team2]);

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      data-test-id={questionId ? `question-${questionId}` : 'prop-pick'}
      data-test-type="prop"
    >
      {/* Team Matchup */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <TeamJersey size="lg" jerseyUrl={teamJerseys.team1Jersey} />
        <span className="text-white text-center font-gilroy text-2xl font-bold leading-8">{t('game.labels.vs')}</span>
        <TeamJersey size="lg" jerseyUrl={teamJerseys.team2Jersey} />
      </div>

      {/* Team Names or Subtext */}
      <div className="text-center mb-4">
        <span data-test-id="prop-matchup" className="text-white text-center font-gilroy text-base font-light leading-5">
          {subtext || `${team1.name} @ ${team2.name}`}
        </span>
      </div>

      {/* Question */}
      <div className="text-center mb-4">
        <span data-test-id="prop-question" className="text-white text-center font-gilroy text-base font-bold leading-5">{question}</span>
      </div>

      {/* Answer Options */}
      <div className="grid grid-cols-2 gap-4">
        <AnswerButton
          answer="yes"
          label={t('game.labels.yes')}
          isSelected={selectedAnswer === 'yes'}
          percentage={percentages?.yes}
          onClick={() => onAnswerSelect('yes')}
          disabled={disabled}
          isCorrect={selectedAnswer === 'yes' ? isCorrect : undefined}
          testId={questionId ? `option-${questionId}-yes` : 'prop-answer-yes'}
        />
        <AnswerButton
          answer="no"
          label={t('game.labels.no')}
          isSelected={selectedAnswer === 'no'}
          percentage={percentages?.no}
          onClick={() => onAnswerSelect('no')}
          disabled={disabled}
          isCorrect={selectedAnswer === 'no' ? isCorrect : undefined}
          testId={questionId ? `option-${questionId}-no` : 'prop-answer-no'}
        />
      </div>
    </QuestionCard>
  );
};

export default PropPick;
